// Chat-first workspace setup — a centered modal, not a page of chrome.
//
// Two quick steps, nothing presumptuous:
//   1. Pick a domain (no side effects — it tunes sample recommendations).
//   2. Confirm the derived workspace name (edit it if you like).
// Deliberately NO "what metric do you want to forecast?" question: the
// exact target is chosen from REAL columns in the chat once data is in,
// so users can never pick a metric their dataset doesn't have.
// Only the confirm creates the workspace. The modal then morphs through a
// "creating" state straight into the Analyst: we refresh the user payload
// so the app shell recognises the new workspace, then client-side navigate
// to /analyst (no hard reload). The picked domain rides along via
// localStorage, so the assistant never re-asks "what do you want to
// predict?" — it opens on the data step with full context.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, ButtonBase, CircularProgress, InputBase, Stack, Typography,
  alpha, lighten, useTheme,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  ArrowBackRounded as BackIcon,
  EditRounded as EditIcon,
} from '@mui/icons-material';

import { isAuthenticated } from '../../utils/localUser';
import {
  USE_CASES, USE_CASE_GROUPS, deriveWorkspaceName,
} from '../../data/sampleData';
import {
  fetchRequest as createWorkspaceRequest,
  clear as clearCreateWorkspace,
} from '../../redux/actions/onboarding/createWorkspace';
import { fetchRequest as getUserRequest } from '../../redux/actions/user/getUser';
import { surfacesFor } from '../signals/askData/uiTokens';

// Read by AskDataPanel's kickoff: the picked domain rides along so the
// assistant skips "what do you want to predict?" and goes straight to
// data. JSON ({domain}) with a legacy plain-string fallback.
const SETUP_CONTEXT_KEY = 'predictai.setup.context';

// If the client-side handoff stalls (user refetch lost, etc.), fall back
// to a hard navigation so the user is never stranded on the modal.
const HANDOFF_FALLBACK_MS = 8000;


const modalIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.985); }
  to   { opacity: 1; transform: none; }
`;

const stepIn = keyframes`
  from { opacity: 0; transform: translateX(14px); }
  to   { opacity: 1; transform: none; }
`;

const shimmerSweep = keyframes`
  0%   { background-position: -320px 0; }
  100% { background-position: calc(320px + 100%) 0; }
`;

const shimmerBarSx = (theme, height = 13) => {
  const dark = theme.palette.mode === 'dark';
  const base  = dark ? 'rgba(233,237,231,0.055)' : 'rgba(40,50,35,0.065)';
  const sheen = dark ? 'rgba(233,237,231,0.09)'  : 'rgba(40,50,35,0.05)';
  const teal  = dark ? 'rgba(94,230,208,0.10)'   : 'rgba(25,117,99,0.09)';
  const lime  = dark ? 'rgba(192,251,110,0.11)'  : 'rgba(108,179,31,0.09)';
  return {
    height,
    borderRadius: `${Math.ceil(height / 2)}px`,
    backgroundColor: base,
    backgroundImage: `linear-gradient(100deg, transparent 20%, ${sheen} 36%, ${teal} 46%, ${lime} 54%, ${sheen} 64%, transparent 80%)`,
    backgroundSize: '320px 100%',
    backgroundRepeat: 'no-repeat',
    animation: `${shimmerSweep} 2.1s cubic-bezier(0.45, 0, 0.25, 1) infinite`,
  };
};


const UseCaseTile = ({ uc, accent, onClick, disabled, dashed = false }) => {
  const theme = useTheme();
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      focusRipple
      sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
        width: '100%', gap: 1, px: 1.25, py: 0.8,
        borderRadius: '11px', textAlign: 'left',
        border: `1.5px ${dashed ? 'dashed' : 'solid'} ${theme.palette.divider}`,
        opacity: disabled ? 0.55 : 1,
        transition: 'border-color 120ms ease, background-color 120ms ease, transform 120ms ease, box-shadow 120ms ease',
        '&:hover': {
          borderColor: alpha(accent, 0.65),
          bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.1 : 0.045),
          transform: 'translateY(-1px)',
          boxShadow: `0 4px 14px -4px ${alpha(accent, 0.35)}`,
        },
      }}
    >
      <Box sx={{
        width: 25, height: 25, borderRadius: '8px', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.18 : 0.1),
        color: theme.palette.mode === 'dark' ? lighten(accent, 0.35) : accent,
      }}>
        <uc.Icon sx={{ fontSize: 14 }} />
      </Box>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.25, flex: 1, color: 'text.primary' }}>
        {uc.label}
      </Typography>
    </ButtonBase>
  );
};


const SetupPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The slice initialises response to {} - treat that as "not loaded yet".
  const rawPayload = useSelector((s) => s.user_getUser?.response);
  const payload = rawPayload && Object.keys(rawPayload).length ? rawPayload : null;
  const userError = useSelector((s) => s.user_getUser?.error);
  const hasWorkspace = (payload?.workspaces || []).length > 0;

  const createSlice = useSelector((s) => s.onboarding_create_workspace) || {};
  // 'domain' -> 'detail' (confirm the name) -> 'creating' -> 'handoff'.
  const [phase, setPhase] = useState('domain');
  const [domainId, setDomainId] = useState(null);
  const [nameEdit, setNameEdit] = useState(null);  // user-touched name, else derived
  const refetchedRef = useRef(false);
  const navigatedRef = useRef(false);

  const ucById = useMemo(() => {
    const m = {};
    USE_CASES.forEach((u) => { m[u.id] = u; });
    return m;
  }, []);

  useEffect(() => {
    dispatch(clearCreateWorkspace());
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated()) window.location.href = '/login.html';
  }, []);

  // Anyone who already has a workspace doesn't belong here (magic-link
  // logins route to /onboarding on profile_completed alone) - the
  // Analyst chat covers data/goal setup for existing workspaces.
  useEffect(() => {
    if (payload && hasWorkspace && phase === 'domain') navigate('/', { replace: true });
  }, [payload, hasWorkspace, phase, navigate]);

  const domainUc = domainId ? ucById[domainId] : null;
  const autoName = domainId ? deriveWorkspaceName(domainId) : '';
  const wsName = nameEdit !== null ? nameEdit : autoName;
  // Deliberately NO metric question here: the exact forecast target is
  // chosen from REAL columns in the chat once data is in, so nobody can
  // pick a metric their dataset doesn't contain.
  const canCreate = phase === 'detail' && Boolean(wsName.trim());

  const pickDomain = (useCaseId) => {
    if (phase !== 'domain') return;
    setDomainId(useCaseId);
    setNameEdit(null);
    setPhase('detail');
  };

  const create = () => {
    if (!canCreate) return;
    setPhase('creating');
    // The assistant's kickoff instruction carries the pick, so the chat
    // opens on the data step, not with a repeated question.
    try {
      localStorage.setItem(SETUP_CONTEXT_KEY, JSON.stringify({
        domain: domainUc.id === 'other' ? '' : domainUc.label,
      }));
    } catch (_e) { /* private mode */ }
    const goalText = domainUc.id === 'other'
      ? 'Forecast key business metrics'
      : `Forecast ${domainUc.label.toLowerCase()}`;
    dispatch(createWorkspaceRequest({
      body: {
        name: wsName.trim(),
        goal: goalText,
        purpose: goalText,
        description: '',
        keywords: [],
        sectors: [],
        data_granularity: 'day',
      },
      meta: { name: wsName.trim(), useCase: domainId, goalText },
    }));
  };

  // Workspace created: persist the id and refresh the user payload so
  // WorkspaceProvider recognises the new workspace without a reload.
  const wsId = createSlice.response?.id || null;
  useEffect(() => {
    if (!wsId || refetchedRef.current) return;
    refetchedRef.current = true;
    try { localStorage.setItem('WORKSPACE_ID', wsId); } catch (_e) { /* private mode */ }
    dispatch(getUserRequest());
  }, [wsId, dispatch]);

  // The refreshed payload now lists the new workspace: fade the modal out
  // and hand over to the Analyst client-side - same canvas, no reload,
  // the guided setup picks up right where this modal left off.
  const workspaceVisible = Boolean(
    wsId && (payload?.workspaces || []).some((w) => w.uid === wsId),
  );
  useEffect(() => {
    if (!workspaceVisible || navigatedRef.current) return undefined;
    navigatedRef.current = true;
    setPhase('handoff');
    const t = setTimeout(() => navigate('/analyst', { replace: true }), 240);
    return () => clearTimeout(t);
  }, [workspaceVisible, navigate]);

  // Safety net: if the refetch never confirms the workspace, hard-navigate.
  useEffect(() => {
    if (!wsId) return undefined;
    const t = setTimeout(() => {
      if (!navigatedRef.current) {
        navigatedRef.current = true;
        window.location.assign('/analyst');
      }
    }, HANDOFF_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [wsId]);

  // Creation failed: drop back to the detail step (the alert stays visible).
  useEffect(() => {
    if (createSlice.error && phase === 'creating') setPhase('detail');
  }, [createSlice.error, phase]);

  if (!payload && !userError) {
    return (
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 12 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (hasWorkspace && phase === 'domain') return null; // redirect in flight

  const s = surfacesFor(theme);
  const accent = theme.palette.mode === 'dark'
    ? (theme.brand?.lime || theme.palette.primary.main)
    : theme.palette.primary.main;
  const busy = phase === 'creating' || phase === 'handoff';

  const fieldSx = {
    width: '100%',
    px: 1.5, py: 0.9,
    borderRadius: '11px',
    border: `1.5px solid ${theme.palette.divider}`,
    fontSize: 13, fontWeight: 600, color: s.text,
    transition: 'border-color 120ms ease',
    '&.Mui-focused': { borderColor: alpha(accent, 0.75) },
    '& input::placeholder': { color: s.faint, fontWeight: 500 },
  };

  return (
    <Box
      sx={{
        // .App is a flex row: without an explicit width this backdrop
        // shrinks to the modal and hugs the left edge.
        width: '100%',
        minHeight: '100vh',
        bgcolor: s.ink,
        backgroundImage: `radial-gradient(circle, ${s.dot} 1px, transparent 1px)`,
        backgroundSize: '22px 22px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        px: 2, py: 4,
      }}
    >
      <Box
        sx={{
          width: '100%', maxWidth: 620,
          borderRadius: '22px',
          border: `1px solid ${s.border}`,
          background: theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${s.panel2}, ${s.panel})`
            : s.panel,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 70px -24px rgba(0,0,0,0.65)'
            : '0 24px 70px -30px rgba(40,50,35,0.28)',
          px: { xs: 2.5, sm: 4 }, py: { xs: 3, sm: 4 },
          animation: `${modalIn} 340ms cubic-bezier(0.2, 0.7, 0.3, 1) both`,
          transition: 'opacity 240ms ease, transform 240ms ease',
          ...(phase === 'handoff' ? { opacity: 0, transform: 'translateY(-6px) scale(0.99)' } : {}),
        }}
      >
        {/* The real brand lockup (same mark + wordmark as the splash). */}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 2.5 }}>
          <Box
            component="img"
            src={theme.logo}
            alt="Predict.ai"
            sx={{
              width: 24, display: 'block',
              filter: theme.palette.mode === 'dark'
                ? 'drop-shadow(0 0 14px rgba(192, 251, 110, 0.28))'
                : 'drop-shadow(0 4px 10px rgba(10, 12, 10, 0.14))',
            }}
          />
          <Typography sx={{ fontSize: 15, fontWeight: 600, letterSpacing: '0.4px', color: s.text }}>
            Predict
            <Box
              component="span"
              sx={{ color: theme.palette.mode === 'dark' ? '#c0fb6e' : '#85d818' }}
            >
              .ai
            </Box>
          </Typography>
        </Stack>

        {createSlice.error && (
          <Alert
            severity="error" sx={{ mb: 2 }}
            onClose={() => dispatch(clearCreateWorkspace())}
          >
            {createSlice.error}
          </Alert>
        )}

        {busy && domainUc ? (
          // Morph state: the wizard collapses into the confirmed workspace
          // plus the Analyst's shimmer language - visually this IS the
          // chat starting.
          <Box sx={{ py: 2, animation: `${modalIn} 260ms ease both` }}>
            <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="center" sx={{ mb: 2.5 }}>
              <Box sx={{
                width: 30, height: 30, borderRadius: '9px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                color: accent,
              }}>
                <domainUc.Icon sx={{ fontSize: 15 }} />
              </Box>
              <Typography sx={{ fontSize: 14.5, fontWeight: 650, color: s.text }}>
                {wsName}
              </Typography>
            </Stack>
            <Stack spacing={1.2} sx={{ maxWidth: 380, mx: 'auto' }}>
              {[{ w: 96, o: 1 }, { w: 82, o: 0.8 }, { w: 56, o: 0.6 }].map(({ w, o }, i) => (
                <Box
                  key={i}
                  sx={{
                    ...shimmerBarSx(theme),
                    width: `${w}%`, opacity: o,
                    animationDelay: `${i * 160}ms`,
                  }}
                />
              ))}
            </Stack>
            <Typography sx={{ mt: 2.5, fontSize: 12, color: s.muted, textAlign: 'center' }}>
              Setting up your workspace…
            </Typography>
          </Box>
        ) : phase === 'detail' && domainUc ? (
          // Step 2: confirm the workspace name. No metric question — the
          // exact target is chosen from real columns once data is in.
          <Box key={domainId} sx={{ animation: `${stepIn} 240ms cubic-bezier(0.2, 0.7, 0.3, 1) both` }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <ButtonBase
                onClick={() => setPhase('domain')}
                focusRipple
                sx={{
                  width: 28, height: 28, borderRadius: '9px', flexShrink: 0,
                  border: `1px solid ${theme.palette.divider}`, color: s.muted,
                  '&:hover': { color: s.text, borderColor: s.border2 },
                }}
                aria-label="Back to domains"
              >
                <BackIcon sx={{ fontSize: 15 }} />
              </ButtonBase>
              <Box sx={{
                width: 25, height: 25, borderRadius: '8px', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: alpha(accent, theme.palette.mode === 'dark' ? 0.16 : 0.1),
                color: accent,
              }}>
                <domainUc.Icon sx={{ fontSize: 13 }} />
              </Box>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: s.text }}>
                {domainUc.label}
              </Typography>
            </Stack>

            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: s.text, lineHeight: 1.3, mb: 2,
            }}>
              Name your workspace
            </Typography>

            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
              <EditIcon sx={{ fontSize: 12, color: s.faint }} />
              <Typography sx={{
                fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', color: s.muted,
              }}>
                Workspace name
              </Typography>
            </Stack>
            <InputBase
              value={wsName}
              onChange={(e) => setNameEdit(e.target.value)}
              placeholder="Name your workspace"
              sx={fieldSx}
              inputProps={{ maxLength: 60 }}
            />

            <ButtonBase
              onClick={create}
              disabled={!canCreate}
              focusRipple
              sx={{
                mt: 2.5, width: '100%', py: 1.2,
                borderRadius: '12px',
                fontSize: 13.5, fontWeight: 700,
                color: theme.palette.mode === 'dark' ? '#101408' : '#fff',
                bgcolor: canCreate ? accent : alpha(accent, 0.35),
                transition: 'background-color 140ms ease, transform 140ms ease, box-shadow 140ms ease',
                ...(canCreate ? {
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: `0 8px 22px -8px ${alpha(accent, 0.6)}`,
                  },
                } : {}),
              }}
            >
              Create workspace
            </ButtonBase>
            <Typography sx={{ mt: 1.25, fontSize: 11, color: s.faint, textAlign: 'center' }}>
              Next: get your data in and see a baseline forecast in minutes.
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{
              fontSize: 19, fontWeight: 700, color: s.text,
              textAlign: 'center', lineHeight: 1.3,
            }}>
              What do you want to predict?
            </Typography>
            <Typography sx={{
              mt: 0.75, mb: 3, fontSize: 12.5, color: s.muted, textAlign: 'center',
            }}>
              Pick a domain — it tunes the sample data and examples for you.
            </Typography>

            <Stack spacing={2}>
              {USE_CASE_GROUPS.map((group) => (
                <Box key={group.id}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '2px', bgcolor: group.accent, flexShrink: 0 }} />
                    <Typography sx={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: s.muted,
                    }}>
                      {group.label}
                    </Typography>
                    <Box sx={{ flex: 1, height: '1px', bgcolor: s.border }} />
                  </Stack>
                  <Box sx={{
                    display: 'grid', gap: 0.75,
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  }}>
                    {group.ids.map((id) => (
                      <UseCaseTile
                        key={id}
                        uc={ucById[id]}
                        accent={group.accent}
                        disabled={busy}
                        onClick={() => pickDomain(id)}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
              <UseCaseTile
                uc={ucById.other}
                accent={theme.brand?.teal || theme.palette.primary.main}
                disabled={busy}
                onClick={() => pickDomain('other')}
                dashed
              />
            </Stack>

            <Typography sx={{ mt: 2.5, fontSize: 11, color: s.faint, textAlign: 'center' }}>
              Nothing is created yet — you&apos;ll confirm the details next.{' '}
              <Box
                component="span"
                role="button" tabIndex={0}
                onClick={() => window.location.assign('/new-workspace')}
                onKeyDown={(e) => { if (e.key === 'Enter') window.location.assign('/new-workspace'); }}
                sx={{ cursor: 'pointer', fontWeight: 600, color: s.muted, '&:hover': { color: s.text } }}
              >
                Prefer a blank start?
              </Box>
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SetupPage;
